import React from 'react';
import { Modal } from 'react-bootstrap';
import { Customer } from 'types/customer';
import Button from 'components/Button';
import { Pencil, Trash2, RotateCcw } from 'lucide-react';
import styles from './Customers.module.scss';

interface ViewCustomerModalProps {
  show: boolean;
  onHide: () => void;
  customer: Customer | null;
  onEdit: () => void;
  onDelete: () => void;
  onRestore: () => void;
  onError: (message: string) => void;
}

function ViewCustomerModal({
  show,
  onHide,
  customer,
  onEdit,
  onDelete,
  onRestore,
}: ViewCustomerModalProps) {
  if (!customer) {
    return null;
  }

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      className={styles.modal}
      contentClassName={styles.modalContent}
      dialogClassName={styles.modalDialog}
    >
      <Modal.Header closeButton className={styles.modalHeader}>
        <Modal.Title className={styles.modalTitle}>
          Customer Details
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className={styles.modalBody}>
        <div className={styles.viewDetails}>
          <div className={styles.detailRow}>
            <strong>Name:</strong>
            <span>{customer.name}</span>
          </div>
          {customer.email && (
            <div className={styles.detailRow}>
              <strong>Email:</strong>
              <span>{customer.email}</span>
            </div>
          )}
          {customer.phone && (
            <div className={styles.detailRow}>
              <strong>Phone:</strong>
              <span>{customer.phone}</span>
            </div>
          )}
          {customer.address && (
            <div className={styles.detailRow}>
              <strong>Address:</strong>
              <span>{customer.address}</span>
            </div>
          )}
          {customer.tinNumber && (
            <div className={styles.detailRow}>
              <strong>TIN Number:</strong>
              <span>{customer.tinNumber}</span>
            </div>
          )}
          {customer.notes && (
            <div className={styles.detailRow}>
              <strong>Notes:</strong>
              <span>{customer.notes}</span>
            </div>
          )}
          <div className={styles.detailRow}>
            <strong>Status:</strong>
            <span>
              {customer.isVoided ? (
                <span className={styles.voidedBadge}>Voided</span>
              ) : (
                <span className={styles.activeBadge}>Active</span>
              )}
            </span>
          </div>
          {customer.isVoided && customer.voidedAt && (
            <div className={styles.detailRow}>
              <strong>Voided At:</strong>
              <span>{formatDate(customer.voidedAt)}</span>
            </div>
          )}
          {customer.isVoided && customer.voidReason && (
            <div className={styles.detailRow}>
              <strong>Void Reason:</strong>
              <span>{customer.voidReason}</span>
            </div>
          )}
          <div className={styles.detailRow}>
            <strong>Created:</strong>
            <span>{formatDate(customer.createdAt)}</span>
          </div>
          <div className={styles.detailRow}>
            <strong>Last Updated:</strong>
            <span>{formatDate(customer.updatedAt)}</span>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className={styles.modalFooter}>
        <Button type="button" variant="outline" onClick={onHide}>
          Close
        </Button>
        {!customer.isVoided && (
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onEdit();
                onHide();
              }}
            >
              <Pencil size={16} style={{ marginRight: '0.5rem' }} />
              Edit
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onDelete();
                onHide();
              }}
              className={styles.deleteButton}
            >
              <Trash2 size={16} style={{ marginRight: '0.5rem' }} />
              Void
            </Button>
          </>
        )}
        {customer.isVoided && (
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onRestore();
              onHide();
            }}
            className={styles.restoreButton}
          >
            <RotateCcw size={16} style={{ marginRight: '0.5rem' }} />
            Restore
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default ViewCustomerModal;
